import { EditLocation } from "@mui/icons-material";
import { Button, Slider, Stack, TextField, Typography } from "@mui/material";
import _ from "lodash";
import React, { SyntheticEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "../shared/Card";
import { MeasurementType } from "../shared/enums/Result";
import { UTP2EventHeadings } from "../shared/enums/UTP2";
import { FormRow } from "../shared/FormLayout/FormRow";
import { UTP2Data, UTP2Events } from "../shared/interfaces/UTP2";
import { getHeadingLimits } from "./Charts/PhaseResolvedFunctions";
import { TabsList } from "./MeasurementDetails";

export const TDR_Default = -999;
const initialPulseButton = "initialPulseButton";
const locationPulseButton = "locationPulseButton";
const endPulseButton = "endPulseButton";

interface GraphControlProps {
  events?: UTP2Events;
  ultrasonicEvents?: UTP2Data;
  measurementType: MeasurementType;
  phaseShift?: number;
  setPhaseShift: (value: number) => void;
  riseTime?: [number, number];
  pulseWidth?: [number, number];
  setRiseTime: (value: [number, number]) => void;
  setPulseWidth: (value: [number, number]) => void;
  tab: TabsList;
  tdr: [number, number, number];
  setTdr: (value: [number, number, number]) => void;
  setTdrId: (value?: number) => void;
  setCableLengthUpdated: (value: boolean) => void;
  resetGraphControl: boolean;
  setResetGraphControl: (value: boolean) => void;
}

export function GraphControl(props: GraphControlProps) {
  const { t } = useTranslation();
  const {
    events,
    setPhaseShift,
    ultrasonicEvents,
    tab,
    tdr,
    setTdr,
    setTdrId,
    resetGraphControl,
    setResetGraphControl,
  } = props;

  const [phaseShiftSlider, setPhaseShiftSlider] = useState<number>(props.phaseShift ?? 0);

  const updatePhaseShiftSlider = (event: Event, newValue: number | number[]) => {
    setPhaseShiftSlider(newValue as number);
    updatePhaseShift(newValue as number);
  };
  const updatePhaseShift = useMemo(
    () =>
      _.debounce((newPhaseShift: number) => {
        setPhaseShift(newPhaseShift);
      }, 300),
    [setPhaseShift],
  );

  const [riseTimeLimits, setRiseTimeLimits] = useState<[number, number]>(
    () => getHeadingLimits(UTP2EventHeadings.RiseTime, events) as [number, number],
  );
  const [pulseWidthLimits, setPulseWidthLimits] = useState<[number, number]>(
    () => getHeadingLimits(UTP2EventHeadings.PulseWidth, events) as [number, number],
  );

  useMemo(() => {
    setRiseTimeLimits(getHeadingLimits(UTP2EventHeadings.RiseTime, events) as [number, number]);
    setPulseWidthLimits(getHeadingLimits(UTP2EventHeadings.PulseWidth, events) as [number, number]);
  }, [events]);

  const [riseTimeSlider, setRiseTimeSlider] = useState<[number, number]>(props.riseTime ?? riseTimeLimits);
  const [pulseWidthSlider, setPulseWidthSlider] = useState<[number, number]>(props.pulseWidth ?? pulseWidthLimits);

  const updateRiseTimeSlider = (event: Event, newValue: number | number[]) => {
    setRiseTimeSlider(newValue as [number, number]);
  };

  const updatePulseWidthSlider = (event: Event, newValue: number | number[]) => {
    setPulseWidthSlider(newValue as [number, number]);
  };

  const updateRiseTime = (event: Event | SyntheticEvent<Element, Event>, newValue: number | number[]) => {
    props.setRiseTime(newValue as [number, number]);
  };

  const updatePulseWidth = (event: Event | SyntheticEvent<Element, Event>, newValue: number | number[]) => {
    props.setPulseWidth(newValue as [number, number]);
  };

  const [propagationSpeed, setpropagationSpeed] = useState<number>(160);

  const handlePropagationSpeed = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number.parseInt(event.currentTarget.value);
    if (Number.isNaN(newValue)) setpropagationSpeed(0);
    else setpropagationSpeed(Number.parseInt(event.currentTarget.value));
  };

  const handleTdrSelection = (event: React.MouseEvent<HTMLButtonElement>) => {
    const newTdr: [number, number, number] = [...tdr];
    if (event.currentTarget.id === initialPulseButton) {
      newTdr[0] = TDR_Default;
      setTdr(newTdr);
    } else if (event.currentTarget.id === locationPulseButton) {
      newTdr[1] = TDR_Default;
      setTdr(newTdr);
    } else if (event.currentTarget.id === endPulseButton) {
      newTdr[2] = TDR_Default;
      setTdr(newTdr);
    }
  };

  const [cableLength, setCableLength] = useState<number>(0);
  const handleCableLengthChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number.parseInt(event.currentTarget.value);
    if (Number.isNaN(newValue)) setCableLength(0);
    else setCableLength(Number.parseInt(event.currentTarget.value));

    setTdrId(2);
    let tdr = props.tdr;
    if (!tdr[0]) tdr[0] = 0;
    const endPulse = 2 * (newValue / propagationSpeed) + tdr[0];
    if (!Number.isNaN(endPulse)) {
      tdr[2] = endPulse;
      setTdr(tdr);
      props.setCableLengthUpdated(true);
    }
  };

  const [dischargeLocation, setDischargeLocation] = useState<string>("");

  useEffect(() => {
    if (!tdr || tdr.some((x) => x === TDR_Default)) return;
    const cableTime = (tdr[2] - tdr[0]) / 2;
    const dischargePos = (tdr[2] - tdr[1]) / 2;
    const dischargeFrac = dischargePos / cableTime;
    const calculatedCableLength = Math.round(cableTime * propagationSpeed);
    if (Number.isNaN(calculatedCableLength)) setCableLength(0);
    else setCableLength(calculatedCableLength);

    if (Number.isNaN(dischargeFrac)) setDischargeLocation("");
    else setDischargeLocation((dischargeFrac * 100).toFixed(1));
  }, [propagationSpeed, tdr]);

  useMemo(() => {
    if (resetGraphControl) {
      tdr[0] = TDR_Default;
      tdr[1] = TDR_Default;
      tdr[2] = TDR_Default;
      setTdr(tdr);
      setTdrId(undefined);
      setCableLength(0);
      setDischargeLocation("");
      setResetGraphControl(false);
    }
  }, [resetGraphControl, tdr, setTdr, setTdrId, setCableLength, setDischargeLocation, setResetGraphControl]);

  return (
    <Card title={t("common.graphControl")}>
      {(events || ultrasonicEvents) &&
        (tab === TabsList.PhaseResolved ||
          tab === TabsList.UltrasonicPhaseResolved ||
          tab === TabsList.PRPS ||
          tab === TabsList.UltrasonicPRPS) && (
          <FormRow
            label={t("common.phaseShift")}
            value={
              <Slider
                onChange={updatePhaseShiftSlider}
                max={180}
                min={-180}
                value={phaseShiftSlider}
                marks={[{ value: 0 }]}
              />
            }
          />
        )}
      {events &&
        (tab === TabsList.PhaseResolved ||
          tab === TabsList.UltrasonicPhaseResolved ||
          tab === TabsList.PRPS ||
          tab === TabsList.UltrasonicPRPS) && (
          <>
            <FormRow
              label={t("common.pulseWidth")}
              value={
                <Stack alignItems={"center"} width={"100%"}>
                  <Typography>{`${pulseWidthSlider[0]} - ${pulseWidthSlider[1]}`}</Typography>
                  <Slider
                    onChange={updatePulseWidthSlider}
                    onChangeCommitted={updatePulseWidth}
                    min={pulseWidthLimits[0]}
                    max={pulseWidthLimits[1]}
                    value={pulseWidthSlider}
                    disableSwap
                  />
                </Stack>
              }
            />
            <FormRow
              label={t("common.riseTime")}
              value={
                <Stack alignItems={"center"} width={"100%"}>
                  <Typography>{`${riseTimeSlider[0]} - ${riseTimeSlider[1]}`}</Typography>
                  <Slider
                    onChange={updateRiseTimeSlider}
                    onChangeCommitted={updateRiseTime}
                    min={riseTimeLimits[0]}
                    max={riseTimeLimits[1]}
                    value={riseTimeSlider}
                    disableSwap
                  />
                </Stack>
              }
            />
          </>
        )}
      {events && tab === TabsList.Waveforms && tdr && (
        <>
          <FormRow
            label={t("common.propagationSpeed")}
            value={
              <Stack direction={"row"} spacing={1}>
                <TextField value={propagationSpeed} onChange={handlePropagationSpeed} size="small" />
              </Stack>
            }
          />
          <FormRow
            label={t("common.initialPulse")}
            value={
              <Stack direction={"row"} spacing={1} alignItems="center">
                <Button
                  id={initialPulseButton}
                  startIcon={<EditLocation color="lightBlue" />}
                  variant="outlined"
                  color="lightBlue"
                  onClick={handleTdrSelection}
                >
                  {t("common.set")}
                </Button>
                <Typography id="initialPulse">
                  {tdr[0] !== TDR_Default ? tdr[0].toFixed(4) : t("common.selectOnGraph")}
                </Typography>
              </Stack>
            }
          />
          <FormRow
            label={t("common.locationPulse")}
            value={
              <Stack direction={"row"} spacing={1} alignItems="center">
                <Button
                  id={locationPulseButton}
                  startIcon={<EditLocation color="lightPink" />}
                  variant="outlined"
                  color="lightPink"
                  onClick={handleTdrSelection}
                >
                  {t("common.set")}
                </Button>
                <Typography id="locationPulse">
                  {tdr[1] !== TDR_Default ? tdr[1].toFixed(4) : t("common.selectOnGraph")}
                </Typography>
              </Stack>
            }
          />
          <FormRow
            label={t("common.endPulse")}
            value={
              <Stack direction={"row"} spacing={1} alignItems="center">
                <Button
                  id={endPulseButton}
                  startIcon={<EditLocation color="lightGreen" />}
                  variant="outlined"
                  color="lightGreen"
                  onClick={handleTdrSelection}
                >
                  {t("common.set")}
                </Button>
                <Typography id="endPulse">
                  {tdr[2] !== TDR_Default ? tdr[2].toFixed(4) : t("common.selectOnGraph")}
                </Typography>
              </Stack>
            }
          />
          <FormRow
            label={t("common.cableLength")}
            value={<TextField id="cableLength" value={cableLength} onChange={handleCableLengthChange} size="small" />}
          />
          <FormRow
            label={t("common.dischargeLocation")}
            value={
              <Typography id="dischargeLocation" data-testid="dischargeLocation">
                {dischargeLocation}
              </Typography>
            }
          />
        </>
      )}
    </Card>
  );
}
